import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { fmtEUR, fmtDate } from "../lib/format";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { RefreshCcw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import ClientTag from "../components/app/ClientTag";
import {
  Card, Button, SectionLabel, StatusPill, NeutralTag,
} from "../components/app/theme";

function ClientFiche({ clientId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!clientId) return;
    api.get(`/clients/${clientId}`).then((r) => setData(r.data)).catch(() => {});
  }, [clientId]);
  if (!data) return null;
  return (
    <Dialog open={!!clientId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        style={{ borderRadius: 12 }}
      >
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-3"
            style={{
              fontFamily: "var(--th-font-serif)",
              fontSize: 22,
              fontWeight: 500,
              color: "var(--th-text)",
            }}
          >
            {data.nom}
            <ClientTag tag={data.anomalie?.tag} />
          </DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <SectionLabel className="mb-2">Contact</SectionLabel>
            <div className="text-sm" style={{ color: "var(--th-text)" }}>{data.contact_nom}</div>
            <div className="text-xs" style={{ color: "var(--th-text-3)" }}>{data.contact_email}</div>

            <SectionLabel className="mt-6 mb-2">Cycle &amp; panier</SectionLabel>
            <div className="text-sm" style={{ color: "var(--th-text)" }}>
              Cycle habituel :{" "}
              <span className="th-num">{data.anomalie?.cycle_jours || "—"} j</span>
            </div>
            <div className="text-sm flex items-center gap-2 flex-wrap" style={{ color: "var(--th-text)" }}>
              Silence actuel :{" "}
              <span className="th-num">{data.anomalie?.silence_jours || "—"} j</span>
              {data.anomalie?.en_anomalie && (
                <StatusPill level="HAUT">Anomalie</StatusPill>
              )}
            </div>
            {data.anomalie?.basket?.tag === "EN_BAISSE" && (
              <div className="text-sm mt-1" style={{ color: "var(--th-moyen-text)" }}>
                Panier moyen :{" "}
                <span className="th-num">{data.anomalie.basket.delta_pct} %</span>
                {" "}(récent {data.anomalie.basket.panier_recent} € vs précédent {data.anomalie.basket.panier_precedent} €)
              </div>
            )}
            <div className="text-sm" style={{ color: "var(--th-text)" }}>
              CA annuel moyen :{" "}
              <span className="th-num">{fmtEUR(data.ca_annuel_moyen)}</span>
            </div>

            {data.notes && (
              <>
                <SectionLabel className="mt-6 mb-2">Notes</SectionLabel>
                <div className="text-sm" style={{ color: "var(--th-text-2)" }}>{data.notes}</div>
              </>
            )}
          </div>
          <div>
            <SectionLabel className="mb-2">Historique commandes (24 mois)</SectionLabel>
            <Card className="max-h-56 overflow-y-auto text-sm">
              {(data.historique_commandes || []).slice().reverse().map((h) => (
                <div
                  key={`hist-${h.date}-${h.montant}`}
                  className="flex justify-between px-3 py-2"
                  style={{ borderBottom: "1px solid var(--th-border)" }}
                >
                  <span className="th-num" style={{ color: "var(--th-text-3)" }}>{fmtDate(h.date)}</span>
                  <span className="th-num" style={{ color: "var(--th-text)" }}>{fmtEUR(h.montant)}</span>
                </div>
              ))}
              {(!data.historique_commandes || data.historique_commandes.length === 0) && (
                <div className="p-3 text-xs" style={{ color: "var(--th-text-3)" }}>
                  Aucune commande.
                </div>
              )}
            </Card>

            <SectionLabel className="mt-6 mb-2">Propales liées</SectionLabel>
            <Card className="max-h-40 overflow-y-auto text-sm">
              {(data.propales || []).map((p) => (
                <div
                  key={p.id}
                  className="flex justify-between px-3 py-2 gap-3"
                  style={{ borderBottom: "1px solid var(--th-border)" }}
                >
                  <span className="th-num text-xs truncate" style={{ color: "var(--th-text-3)" }}>
                    {p.numero}
                  </span>
                  <span className="truncate" style={{ color: "var(--th-text-2)" }}>{p.statut}</span>
                  <span className="th-num" style={{ color: "var(--th-text)" }}>{fmtEUR(p.montant_ht)}</span>
                </div>
              ))}
              {(!data.propales || data.propales.length === 0) && (
                <div className="p-3 text-xs" style={{ color: "var(--th-text-3)" }}>
                  Aucune propale.
                </div>
              )}
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyAnomalies, setOnlyAnomalies] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients");
      setRows(r.data.clients);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((c) => {
      if (onlyAnomalies && !c.anomalie?.en_anomalie) return false;
      if (q && !c.nom.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [rows, onlyAnomalies, q]);

  const inputStyle = {
    backgroundColor: "var(--th-surface)",
    border: "1px solid var(--th-border-strong)",
    borderRadius: 8,
  };

  return (
    <div className="th-scope p-6 md:p-10" style={{ backgroundColor: "var(--th-bg)" }}>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <SectionLabel>Base de données</SectionLabel>
          <h1
            className="mt-1"
            style={{
              fontFamily: "var(--th-font-serif)",
              fontSize: 27,
              fontWeight: 500,
              letterSpacing: "-0.005em",
              color: "var(--th-text)",
            }}
          >
            Clients
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--th-text-2)" }}>
            {filtered.length} sur {rows.length}
          </p>
        </div>
        <Button onClick={load} variant="secondary">
          <RefreshCcw size={14} /> Actualiser
        </Button>
      </header>

      <Card className="p-5 mb-5 flex flex-col md:flex-row gap-5 items-start md:items-end">
        <div className="flex-1 w-full">
          <SectionLabel className="mb-1.5">Recherche</SectionLabel>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nom du client"
            style={inputStyle}
          />
        </div>
        <label className="flex items-center gap-3 pb-2">
          <Switch
            checked={onlyAnomalies}
            onCheckedChange={setOnlyAnomalies}
            data-testid="filter-anomalies"
          />
          <span className="text-sm" style={{ color: "var(--th-text)" }}>
            Uniquement anomalies de cycle
          </span>
        </label>
      </Card>

      <Card className="overflow-x-auto">
        <table className="th-table w-full">
          <thead>
            <tr>
              <th className="text-left">Client</th>
              <th className="text-left">Tag</th>
              <th className="text-left">Contact</th>
              <th className="text-right">Dernière commande</th>
              <th className="text-right">Cycle</th>
              <th className="text-right">Silence</th>
              <th className="text-center">Anomalie</th>
              <th className="text-right">CA annuel</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="p-6 text-center" style={{ color: "var(--th-text-3)" }}>
                  Chargement…
                </td>
              </tr>
            )}
            {!loading && filtered.map((c) => (
              <tr
                key={c.id}
                onClick={() => setSelected(c.id)}
                style={{ cursor: "pointer" }}
              >
                <td style={{ fontWeight: 500 }}>{c.nom}</td>
                <td><ClientTag tag={c.anomalie?.tag} /></td>
                <td className="text-xs" style={{ color: "var(--th-text-2)" }}>{c.contact_nom}</td>
                <td className="th-num text-right">{fmtDate(c.anomalie?.derniere_commande_date)}</td>
                <td className="th-num text-right">{c.anomalie?.cycle_jours || "—"}j</td>
                <td className="th-num text-right">{c.anomalie?.silence_jours ?? "—"}j</td>
                <td className="text-center">
                  {c.anomalie?.en_anomalie
                    ? <StatusPill level="HAUT">Oui</StatusPill>
                    : <NeutralTag>Non</NeutralTag>}
                </td>
                <td className="th-num text-right">{fmtEUR(c.ca_annuel_moyen)}</td>
                <td className="text-right">
                  <ExternalLink size={14} className="inline" style={{ color: "var(--th-text-3)" }} />
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="p-8 text-center text-sm" style={{ color: "var(--th-text-3)" }}>
                  Aucun client.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <ClientFiche clientId={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
